import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { FixedSizeList as List } from "react-window";
import "./css/FileRainbow.css";

const colors = [
  "#e2a458",
  "#508950",
  "#c995c6",
  "#dcdcaa",
  "#569cd6",
  "#40caff",
  "#b986e3",
  "#ff6f5f",
  "#9fa14e",
  "#b5cea8",
  "#f44747",
  "#6a9955",
  "#ce9178",
  "#4ec9b0",
  "#9cdcfe",
];

const FixedWidthViewer = ({
  fileContent,
  layout,
  screenHeight,
  lineHeight = 24,
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [fieldData, setFieldData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const listRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const setCursorPosition = (event) => {
    setCursor({
      x: event.clientX,
      y: event.clientY,
    });
  };

  function validateFields(fields) {
    const sorted = [...fields].sort((a, b) => a.index - b.index);
    const conflicts = [];

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      const currentEnd = current.index + current.size - 1;

      if (next.index <= currentEnd) {
        conflicts.push({
          fieldA: current,
          fieldB: next,
          overlapStart: next.index,
          overlapEnd: Math.min(currentEnd, next.index + next.size - 1),
        });
      }
    }

    return conflicts;
  }

  const lines = useMemo(() => {
    if (!fileContent) return [];
    return fileContent.split("\n");
  }, [fileContent]);

  const registerLength = Object.keys(layout.registers)[0]?.length || 0;

  const lineNumberWidth = useMemo(() => {
    const maxDigits = lines.length.toString().length;
    return maxDigits * 10 + 16;
  }, [lines.length]);

  const renderLine = useCallback(
    (line, lineNumber) => {
      if (!layout) return null;

      const key = line.substring(0, registerLength);
      const elements = [];
      const lineNumbers = [];

      lineNumbers.push(
        <span
          key={`lineNumber-${lineNumber}`}
          style={{ color: "silver", marginRight: 8 }}
        >
          {lineNumber}
        </span>,
      );

      if (!key || !layout?.registers?.[key]?.fields?.length) {
        return (
          <div style={{ display: "flex" }}>
            {/*  <span style={{ visibility: "hidden", marginRight: 6 }}>⚠</span> */}
            <div
              style={{
                width: lineNumberWidth,
                textAlign: "right",
                paddingRight: 8,
                color: "silver",
                flexShrink: 0,
              }}
            >
              {lineNumber}
            </div>
            <div style={{ whiteSpace: "pre" }}>
              {selectedValue && line.includes(selectedValue) ? (
                <>
                  {line.split(selectedValue).map((part, index, arr) => (
                    <span key={index}>
                      {part}
                      {index < arr.length - 1 && (
                        <span className="selected-field">{selectedValue}</span>
                      )}
                    </span>
                  ))}
                </>
              ) : (
                line
              )}
            </div>
          </div>
        );
      }

      const fields = [...layout?.registers?.[key]?.fields].sort(
        (a, b) => a.index - b.index,
      );
      const conflicts = validateFields(fields);
      const conflictedFields = new Set();

      conflicts.forEach((c) => {
        conflictedFields.add(c.fieldA.name);
        conflictedFields.add(c.fieldB.name);
      });

      let currentPosition = 0;
      let lastColor = 0;

      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const start = field.index - 1;
        const end = start + field.size;
        const isConflict = conflictedFields.has(field.name);

        if (currentPosition < start) {
          elements.push(
            <span key={`text-${lineNumber}-${i}`}>
              {line.substring(currentPosition, start)}
            </span>,
          );
        }

        const value = line.substring(start, end);
        const isSelected = selectedValue === value;

        elements.push(
          <span
            key={`field-${lineNumber}-${i}`}
            className={`fixed-field ${isSelected ? "selected-field" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedValue(value);
            }}
            onMouseEnter={(e) => {
              setCursorPosition(e);
              setFieldData({
                field,
                key,
                end,
                value,
                lineNumber,
              });
            }}
            onMouseLeave={() => {
              setFieldData(null);
            }}
            style={{
              color: isConflict ? "#ff4d4f" : colors[lastColor],
              cursor: "pointer",
              textDecoration: isConflict ? "underline wavy red" : "none",
            }}
          >
            {value}
          </span>,
        );

        currentPosition = end;
        lastColor = (lastColor + 1) % colors.length;
      }

      if (currentPosition < line.length) {
        elements.push(
          <span key={`rest-${lineNumber}`}>
            {line.substring(currentPosition)}
          </span>,
        );
      }

      return (
        <div style={{ display: "flex" }}>
          {/*           {conflicts.length > 0 && (
            <Tooltip
              arrow
              title={
                <div>
                  {conflicts.map((c, idx) => (
                    <div key={idx}>
                      {c.fieldA.name} sobrepõe {c.fieldB.name}
                      <br />
                      Intervalo: {c.overlapStart}-{c.overlapEnd}
                    </div>
                  ))}
                </div>
              }
            >
              <span style={{ color: "#ff4d4f", marginRight: 6 }}>⚠</span>
            </Tooltip>
          )} */}
          <div
            style={{
              width: lineNumberWidth,
              textAlign: "right",
              paddingRight: 8,
              color: "silver",
              flexShrink: 0,
            }}
          >
            {lineNumber}
          </div>

          <div style={{ whiteSpace: "pre" }}>{elements}</div>
        </div>
      );
    },
    [layout, registerLength, selectedValue],
  );

  const Row = ({ index, style }) => {
    return <div style={style}>{renderLine(lines[index], index + 1)}</div>;
  };

  if (!fileContent || !layout) return null;

  return (
    <Box
      onClick={() => setSelectedValue(null)}
      className="rainbow-container"
      sx={{
        height: screenHeight - 167 + "px",
        border: "solid 1px #c8c8c85c",
        marginTop: "34px",
        userSelect: "text",
        fontFamily: "monospace",
        fontSize: 14,
        paddingTop: "8px",
      }}
      m={1}
    >
      <div>
        {fieldData && (
          <>
            {/*             <div className="field-data">
              <Typography sx={{ mr: 2 }} variant="caption">
                <u>
                  <strong>{fieldData.field.name}</strong>
                </u>
              </Typography>
              <Typography sx={{ mr: 2 }} variant="caption">
                Registro: {fieldData.key}
              </Typography>
              <Typography sx={{ mr: 2 }} variant="caption">
                Posição: {fieldData.field.index} - {fieldData.end}
              </Typography>{" "}
              <Typography sx={{ mr: 2 }} variant="caption">
                Tamanho: {fieldData.field.size}
              </Typography>
              <Typography sx={{ mr: 2 }} variant="caption">
                Linha: {fieldData.lineNumber}
              </Typography>
            </div>
 */}
            <div
              className="field-data-tooltip"
              style={{
                left:
                  cursor.x > window.innerWidth - 150
                    ? cursor.x - 150
                    : cursor.x + 20,
                top:
                  cursor.y > screenHeight - 140
                    ? cursor.y - 120
                    : cursor.y + 20,
              }}
            >
              <Typography variant="caption">
                <u>
                  <strong>{fieldData.field.name}</strong>
                </u>
              </Typography>
              <br />
              <Typography variant="caption">
                Registro: {fieldData.key}
              </Typography>{" "}
              <br />
              <Typography variant="caption">
                Posição: {fieldData.field.index} - {fieldData.end}
              </Typography>{" "}
              <br />
              <Typography variant="caption">
                Tamanho: {fieldData.field.size}
              </Typography>
              <br />
              <Typography sx={{ mr: 2 }} variant="caption">
                Linha: {fieldData.lineNumber}
              </Typography>
            </div>
          </>
        )}

        <List
          ref={listRef}
          height={screenHeight - 167}
          itemCount={lines.length}
          itemSize={lineHeight}
          width="100%"
        >
          {Row}
        </List>
      </div>
    </Box>
  );
};

export default FixedWidthViewer;
